import { Config } from '@/config';
import apiService from '@/services/api.service';
import type { ContextDto } from '@/services/types/api.types';
import { defineStore } from 'pinia';
import { Constants } from '@/constants';
import { useI18n } from 'vue-i18n';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { getSupportedLocales } from '@/plugins/i18n';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';

const parentMapping: { [key: number]: string } = {
  1: 'consultations',
  2: 'sections',
  3: 'forms',
  4: 'project-modules',
  5: 'event-items',
};

const getParentKey = (parent: number, parentId: number) => `${getParentName(parent)}-${parentId}`;
const getParentName = (parent: number) => parentMapping[parent] || 'unknown';

export interface ContextsState {
  contexts: { [parentKey: string]: ContextDto[] };
  itemLoadingStates: Set<string>;
}

function getTypes(): any[] {
  const { t } = useI18n();
  return [
    { value: 1, label: t('context.types.texte') },
    {
      value: 2,
      label: t('context.types.image'),
    },
    {
      value: 3,
      label: t('context.types.video'),
    },
    { value: 5, label: t('context.types.file') },
    { value: 6, label: t('context.types.link') },
  ];
}

export const useContextsStore = defineStore({
  id: 'contexts',
  state: (): ContextsState => ({
    contexts: {},
    itemLoadingStates: new Set<string>(),
  }),
  getters: {
    isItemLoading: (state) => (parent: number, parentId: number) =>
      state.itemLoadingStates.has(getParentKey(parent, parentId)),
    getOrderedContexts: (state) => (parent: number, parentId: number) =>
      state.contexts[getParentKey(parent, parentId)] || [],
    getTypes: (state) => getTypes,
  },
  actions: {
    async fetchContextsByParent(parent: number, parentId: number) {
      const parentKey = getParentKey(parent, parentId);
      const parentName = getParentName(parent);
      this.itemLoadingStates.add(parentKey);
      try {
        const { data } = await apiService.get<ContextDto[]>(`${Config.api.v2}/${parentName}/${parentId}/contexts`);
        this.contexts[parentKey] = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(parentKey);
      }
    },
    createContext(parent: number, parentId: number): ContextDto {
      return {
        id: null,
        parent,
        parent_id: parentId,
        type: 1,
        title: { en: '', fr: '' },
        content: { en: '', fr: '' },
        order_num: -1,
      };
    },
    createContextData(context: ContextDto): {
      data: { context: ContextDto; files: { language: string; filename: string }[] };
      files: File[] | undefined;
    } {
      const files: File[] = [];
      const fileList: any[] = [];
      if (context.type === Constants.Context.Type.Image || context.type === Constants.Context.Type.File) {
        if (context.content_files) {
          getSupportedLocales().forEach((locale) => {
            if (context.content_files?.[locale]) {
              const content = context.content_files[locale] as unknown as File[];
              content?.forEach((file: File) => {
                if (file instanceof File) {
                  files.push(file);
                  fileList.push({ language: locale, filename: file.name });
                }
              });
            }
          });
        }
      }
      return { data: { context, files: fileList }, files: files.length > 0 ? files : undefined };
    },
    async saveContext(options: SavingOptions): Promise<boolean> {
      const data = options.data as { context: ContextDto };
      const context = data.context;
      const url = context?.id ? `${Config.api.v2}/contexts/${context.id}` : `${Config.api.v2}/contexts`;
      try {
        await FileUploaderFactory.create(options.version).upload({
          data,
          files: options.files,
          method: apiService.post,
          processorType: ProcessorType.Formidable,
          url,
        });
        this.fetchContextsByParent(context.parent, context.parent_id);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteContext(context: ContextDto) {
      try {
        await apiService.delete(`${Config.api.v2}/contexts/${context.id}`);
        this.fetchContextsByParent(context.parent, context.parent_id);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
